<script lang="ts" setup>
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import Menu from 'primevue/menu';
import type { MenuProps } from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref, type MaybeRefOrGetter } from 'vue';
import { useApplicationRoot } from '@tools/applicationRoot';
import { type VMenuTheme, getStylesObj } from './VMenu.styles';

import VTooltip from '../VTooltip/VTooltip.vue';

export type VMenuItem = Omit<MenuItem, 'icon'> & {
  badge?: string;
  icon?: FontAwesomeIconProps['icon'];
  disabledReason?: MaybeRefOrGetter<string>;
};

export type VMenuProps = {
  appendTo?: MenuProps['appendTo'];
  model?: VMenuItem[];
  popup?: MenuProps['popup'];
  pt?: MenuProps['pt'];
  ptOptions?: MenuProps['ptOptions'];
  theme?: VMenuTheme;
};

const props = defineProps<VMenuProps>();

const menu = ref();

const applicationRoot = useApplicationRoot();

const componentProps = computed(() =>
  Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'icon')),
);

const stylesObj = computed(() => getStylesObj(props.theme));

defineExpose({
  hide: (event: Event) => menu.value.hide(event),
  show: (event: Event) => menu.value.show(event),
  toggle: (event: Event) => menu.value.toggle(event),
  overlayVisible: () => menu.value.overlayVisible,
});
</script>

<template>
  <Menu
    v-bind="componentProps"
    ref="menu"
    data-testid="menu"
    :append-to="appendTo ?? applicationRoot"
  >
    <template #start>
      <slot name="start"></slot>
    </template>

    <template #item="{ item, props }">
      <slot name="item" :item="item" :props="props" :styles="stylesObj">
        <VTooltip v-if="item.disabled && item.disabledReason" :label="item.disabledReason">
          <a v-bind="props.action" :target="item.target" :href="item.url">
            <i v-bind="props.icon" class="mr-2"></i>
            <span v-bind="props.label">{{ item.label }}</span>
          </a>
        </VTooltip>
        <a v-else v-bind="props.action" :target="item.target" :href="item.url">
          <i v-bind="props.icon" class="mr-2"></i>
          <span v-bind="props.label">{{ item.label }}</span>
        </a>
      </slot>
    </template>
  </Menu>
</template>
